import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import Modal from "components/Modal";
import { IconClock } from "@tabler/icons-react";
import { mappingSnapshotStore } from "./MappingSnapshotStore";
import useToast from "hooks/useToast";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import ConfirmModal from "components/ConfirmModal";
import Alert from "components/Alert";
import TakeSnapshotControl from "./TakeSnapshotControl";
import SnapshotList from "./SnapshotList";

interface MappingFullSnapshotsModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  fileId: string;
}

function MappingSnapshotsModal({
  isOpen,
  setIsOpen,
  fileId,
}: MappingFullSnapshotsModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const {
    snapshots,
    snapshotsLoading,
    restoringId,
    loading: storeLoading,
    hasMoreSnapshots,
  } = mappingSnapshotStore;

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<number | null>(
    null
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteSnapshotId, setSelectedDeleteSnapshotId] = useState<
    number | null
  >(null);
  const LARGE_SNAPSHOT_OBJECT_COUNT = 2000;

  const handleLoadMore = () => {
    mappingSnapshotStore.listSnapshots(fileId);
  };

  const handleTakeSnapshot = async (snapshotName: string) => {
    const result = await mappingSnapshotStore.takeSnapshot(
      fileId,
      snapshotName,
      toast,
      t("mappingEditor.snapshotCreated")
    );
    if (!result.ok) {
      toast(result.message || t("commons.genericErrorMessage"), "warning");
      return false;
    }
    return true;
  };

  const openConfirmModal = (id: number) => {
    setSelectedSnapshotId(id);
    setConfirmModalOpen(true);
  };

  const openDeleteModal = (id: number) => {
    setSelectedDeleteSnapshotId(id);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (selectedDeleteSnapshotId === null) return;
    const result = await mappingSnapshotStore.deleteSnapshot(
      fileId,
      selectedDeleteSnapshotId,
      toast,
      t("mappingEditor.snapshotDeleted")
    );
    if (!result.ok) {
      toast(result.message || t("commons.genericErrorMessage"), "warning");
      return;
    }
    setDeleteModalOpen(false);
  };

  const handleRestore = async () => {
    if (selectedSnapshotId === null) return;

    const restoreResult = await mappingSnapshotStore.restoreSnapshot(
      fileId,
      selectedSnapshotId,
      toast,
      t("mappingEditor.snapshotRestored")
    );

    if (!restoreResult.ok) {
      toast(
        restoreResult.message || t("commons.genericErrorMessage"),
        "warning"
      );
      return;
    }

    setConfirmModalOpen(false);
    setIsOpen(false);
  };

  const selectedSnapshot = snapshots.find(
    (snapshot) => snapshot.id === selectedSnapshotId
  );
  const showLargeSnapshotWarning =
    (selectedSnapshot?.number_matches ?? 0) > LARGE_SNAPSHOT_OBJECT_COUNT;

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={t("mappingEditor.savedSnapshots")}
        leadingIcon={<IconClock size={18} className="text-gray-500 mr-2" />}
        containerClassName="sm:max-w-lg h-fit"
        scrollable
        disableOutsideClick
      >
        <div className="p-6 space-y-4">
          <Alert
            type="info"
            description={t("mappingEditor.autoSnapshotInfo")}
          />

          <TakeSnapshotControl
            isOpen={isOpen}
            loading={storeLoading}
            onSubmit={handleTakeSnapshot}
          />

          <SnapshotList
            snapshots={snapshots}
            loading={snapshotsLoading}
            hasMore={hasMoreSnapshots}
            restoringId={restoringId}
            onDelete={openDeleteModal}
            onRestore={openConfirmModal}
            onLoadMore={handleLoadMore}
          />
          {snapshotsLoading && snapshots.length === 0 && (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          )}
        </div>
      </Modal>

      <ConfirmModal
        open={confirmModalOpen}
        close={() => setConfirmModalOpen(false)}
        title={t("mappingEditor.restoreConfirmTitle")}
        descriptionMesage={
          <div className="space-y-2">
            <span>{t("mappingEditor.restoreConfirmDescription")}</span>
            {showLargeSnapshotWarning && (
              <span className="block text-yellow-700">
                {t("mappingEditor.restoreMayTakeAWhile")}
              </span>
            )}
          </div>
        }
        confirmButtonTitle={t("mappingEditor.restore")}
        onConfirm={handleRestore}
        type="warning"
      />

      <ConfirmModal
        open={deleteModalOpen}
        close={() => setDeleteModalOpen(false)}
        title={t("mappingEditor.deleteConfirmTitle")}
        descriptionMesage={t("mappingEditor.deleteConfirmDescription")}
        confirmButtonTitle={t("mappingEditor.delete")}
        onConfirm={handleDelete}
        type="error"
        confirmButtonType="error"
      />
    </>
  );
}

export default observer(MappingSnapshotsModal);
